import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import BackToTop from "@/components/BackToTop";
import ScrollToTopOnNav from "@/components/ScrollToTopOnNav";
import BuildPath from "@/pages/BuildPath";
import Journal from "@/pages/Journal";
import KanjiFocus from "@/pages/KanjiFocus";
import VocabFocus from "@/pages/VocabFocus";
import Study from "@/pages/Study";
import PhoneticAtlas from "@/pages/PhoneticAtlas";
import Welcome from "@/pages/Welcome";
import { PrivacyPolicy, TermsOfService } from "@/pages/Legal";
import Guide from "@/pages/Guide";
import { DemoProvider, useDemo } from "@/demo/DemoProvider";
import DemoNarrator from "@/demo/DemoNarrator";
import { AuthProvider } from "@/auth/AuthProvider";
import EntitlementGate from "@/auth/EntitlementGate";

// LocalStorage flag set when the user *explicitly* dismisses the
// Welcome page (clicks "Skip to the app" or "Take the guided tour").
// Bumped to v2 because v1 was set too eagerly (on any non-/welcome
// page visit), which permanently bypassed the marketing surface for
// anyone who deep-linked or refreshed mid-session. v2 only gets set
// from inside Welcome.jsx itself, so cold launches always land on
// /welcome until the user opts out.
const SEEN_FLAG = "kb_seen_app_v2";

function FirstVisitRedirect() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname !== "/") return;
    let seen = false;
    try { seen = localStorage.getItem(SEEN_FLAG) === "1"; } catch (e) {
      // Private-browsing / storage disabled — fall back to "not seen"
      // so the marketing surface still works.
      console.warn("localStorage read failed for", SEEN_FLAG, e);
    }
    if (!seen) navigate("/welcome", { replace: true });
  }, [pathname, navigate]);
  return null;
}

function Shell() {
  const navigate = useNavigate();
  const location = useLocation();
  const goToKanji = (ch, kind) => {
    if (kind === "word" || (ch && ch.length > 1)) {
      navigate(`/word/${encodeURIComponent(ch)}`);
    } else {
      navigate(`/kanji/${encodeURIComponent(ch)}`);
    }
  };

  // /welcome renders standalone — no app header / nav chrome. Everything
  // else lives inside the standard Layout.
  // /auth/google is the Google OAuth callback — also chromeless; the
  // AuthProvider effect handles the code exchange and navigates onward.
  const isWelcome = location.pathname === "/welcome";
  const isAuthCallback = location.pathname === "/auth/google";
  // Legal pages are chromeless too — they need to be reachable without
  // EntitlementGate (Google's OAuth consent screen requires the URLs to
  // be publicly accessible).
  const isLegal = location.pathname === "/privacy" || location.pathname === "/terms";
  // The User Guide is a public / chromeless surface too — it needs to be
  // reachable without EntitlementGate so both prospective users on the
  // Welcome page and search-engine crawlers can read it end-to-end.
  const isGuide = location.pathname === "/guide";

  return (
    <DemoProvider>
      <AuthProvider>
        <ScrollToTopOnNav />
        <FirstVisitRedirect />
        {isWelcome ? (
          <Routes>
            <Route path="/welcome" element={<Welcome />} />
          </Routes>
        ) : isAuthCallback ? (
          <AuthCallbackPage />
        ) : isLegal ? (
          <Routes>
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
          </Routes>
        ) : isGuide ? (
          <Routes>
            <Route path="/guide" element={<Guide />} />
          </Routes>
        ) : (
          <GatedLayout onSelectKanji={goToKanji} />
        )}
        <BackToTop />
        <DemoNarrator />
      </AuthProvider>
    </DemoProvider>
  );
}

/**
 * Tiny chromeless landing page shown for the ~250ms while the
 * AuthProvider exchanges Google's authorization code for our session
 * cookie. The provider navigates away on success or failure.
 */
function AuthCallbackPage() {
  return (
    <div
      data-testid="auth-callback-page"
      className="min-h-screen flex items-center justify-center bg-washi text-sumi font-serif"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="font-serif text-3xl text-shu">寺</div>
        <p className="text-sm tracking-[0.25em] uppercase text-sumiSoft">signing you in…</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
      <Toaster position="bottom-right" theme="light" />
    </div>
  );
}

/**
 * Reads demo status from context so the entitlement gate can step
 * aside during the guided tour. Kept tiny — pure plumbing.
 */
function GatedLayout({ onSelectKanji }) {
  const demo = useDemo();
  const demoActive = demo?.status && demo.status !== "idle";
  return (
    <Layout onSelectKanji={onSelectKanji}>
      <EntitlementGate demoActive={demoActive}>
        <Routes>
          <Route path="/" element={<BuildPath onSelectKanji={onSelectKanji} />} />
          <Route path="/kanji/:ch" element={<KanjiFocus />} />
          <Route path="/word/:word" element={<VocabFocus />} />
          <Route path="/study" element={<Study />} />
          <Route path="/atlas" element={<PhoneticAtlas onSelectKanji={onSelectKanji} />} />
          <Route path="/journal" element={<Journal onSelectKanji={onSelectKanji} />} />
        </Routes>
      </EntitlementGate>
    </Layout>
  );
}

export default App;

